// Post-processing pipeline: bloom for magic/faith effects, outline for
// selection, and an optional doctrine-driven color grade via HueSaturation.
import {
  Bloom,
  EffectComposer,
  HueSaturation,
  Outline,
  Vignette,
} from '@react-three/postprocessing';
import { BlendFunction, KernelSize } from 'postprocessing';
import type { ReactElement } from 'react';
import type { Archetype } from '../ui/DoctrinePanel/index.js';
import { getDoctrineColorGrade } from './postprocessing/ColorGradeLUT.js';

interface PostProcessingProps {
  readonly hasSelection?: boolean;
  readonly doctrine?: Archetype | null;
}

export function PostProcessing({ hasSelection = true, doctrine = null }: PostProcessingProps) {
  const grade = getDoctrineColorGrade(doctrine ?? null);
  const gradeActive = doctrine !== null && doctrine !== 'science';
  // EffectComposer's children type is `JSX.Element | JSX.Element[]` (strict —
  // no null/false allowed), so build the array explicitly.
  const effects: ReactElement[] = [
    <Bloom
      key="bloom"
      luminanceThreshold={0.85}
      luminanceSmoothing={0.3}
      intensity={0.5}
      mipmapBlur
      // Use the named enums: the numeric values shift between postprocessing
      // releases. A literal `4` meant SCREEN once but is COLOR_BURN in 6.39,
      // which burned the whole scene to black under the mostly-empty bloom
      // buffer (the black-on-black map).
      blendFunction={BlendFunction.SCREEN}
      kernelSize={KernelSize.MEDIUM}
    />,
    // Gentle corner falloff: frames the board without dimming play space.
    // Kept light so the night half of the day/night cycle stays legible.
    <Vignette key="vignette" offset={0.3} darkness={0.45} eskil={false} />,
  ];
  if (gradeActive) {
    effects.push(<HueSaturation key="grade" hue={grade.hue} saturation={grade.saturation} />);
  }
  if (hasSelection) {
    effects.push(
      <Outline
        key="outline"
        visibleEdgeColor={0xffd966}
        hiddenEdgeColor={0xffd966}
        edgeStrength={2}
        pulseSpeed={1.5}
        blur={false}
      />,
    );
  }
  return <EffectComposer multisampling={4}>{effects}</EffectComposer>;
}
